import React, {useEffect, useMemo} from "react";
import {Button, Pagination, Popover, Spin, Table, Tooltip} from "antd";
import {ColumnsType} from "antd/es/table";
import styled from "styled-components";
import {CloseOutlined, EyeFilled, SearchOutlined} from "@ant-design/icons";
import Search from "antd/es/input/Search";
import {useSelector} from "react-redux";
import {selectEduType} from "../../redux/slices/appSlice";
import {ENUM_ACCOUNT_TYPE} from "../../common/Constants";

type Props = {
    isRequesting: boolean
    round1?: number
    rawDataTable: any
    searchValue?: {
        userId?: number,
        fullName?: string,
        schoolName?: string
    }
    onSearch: (page: number, userId?: number, fullName?: string, schoolName?: string) => void
}

export const ListUser: React.FC<Props> = ({isRequesting, round1, rawDataTable, searchValue, onSearch}) => {

    const selEduType = useSelector(selectEduType);

    const [isSearchID, setIsSearchID] = React.useState<boolean>(false)
    const [isSearchName, setIsSearchName] = React.useState<boolean>(false)
    const [isSearchSchool, setIsSearchSchool] = React.useState<boolean>(false)
    useEffect(() => {
        setIsSearchID(false);
        setIsSearchName(false);
        setIsSearchSchool(false);
    }, [rawDataTable])

    const columns = useMemo((): ColumnsType<any> => [
        {
            title: "Xếp hạng",
            dataIndex: "village_Rank",
            key: "rank",
            align: "center"
        },
        {
            title: <div className={"flex items-center gap-2"}>
                <div className={"w-full text-center"}>ID</div>
                {selEduType === ENUM_ACCOUNT_TYPE.EduManager_Phong
                    && <Popover
                        content={<div className={"relative"}>
                            <Button
                                type="text"
                                icon={<CloseOutlined size={8}/>}
                                className={'absolute right-[-1.5rem] top-[-1.5rem] z-10'}
                                style={styleClose}
                                onClick={() => setIsSearchID(false)}
                            />
                            <Search className={"max-w-[200px]"}
                                    placeholder="Nhập ID tìm kiếm"
                                    value={searchValue?.userId}
                                    disabled={isRequesting}
                                    onSearch={value => onSearch(1, parseInt(value) || 0, undefined, undefined)}/>
                        </div>}
                        trigger="click"
                        open={isSearchID}
                    >
                        <SearchOutlined
                            className={"cursor-pointer text-[#0095DA]"}
                            onClick={() => {
                                setIsSearchID(true);
                                setIsSearchName(false);
                                setIsSearchSchool(false);
                            }}/>
                    </Popover>}
            </div>,
            dataIndex: "userId",
            key: "userId"
        },
        {
            title: <div className={"flex items-center gap-2"}>
                <div className={"w-full text-center"}>
                    Họ và Tên
                </div>
                {selEduType === ENUM_ACCOUNT_TYPE.EduManager_Phong &&
                    <Popover
                        content={<div className={"relative"}>
                            <Button
                                type="text"
                                icon={<CloseOutlined size={8}/>}
                                className={'absolute right-[-1.5rem] top-[-1.5rem] z-10'}
                                style={styleClose}
                                onClick={() => setIsSearchName(false)}
                            />
                            <Search className={"max-w-[200px]"}
                                    placeholder="Nhập tên tìm kiếm"
                                    defaultValue={searchValue?.fullName}
                                    disabled={isRequesting}
                                    onSearch={value => onSearch(1, undefined, value, undefined)}/>
                        </div>}
                        trigger={"click"}
                        open={isSearchName}>
                        <SearchOutlined
                            className={"cursor-pointer text-[#0095DA]"}
                            onClick={() => {
                                setIsSearchName(true);
                                setIsSearchID(false);
                                setIsSearchSchool(false);
                            }}/>
                    </Popover>}
            </div>,
            dataIndex: "fullName",
            key: "fullName"
        },
        {
            title: "Lớp",
            dataIndex: "className",
            key: "className",
            align: "center"
        },
        {
            title: "Khối",
            dataIndex: "block",
            key: "block",
            align: "center"
        },
        {
            title: <div className={"flex items-center gap-2"}>
                <div className={"w-full text-center"}>
                    Trường
                </div>
                {selEduType === ENUM_ACCOUNT_TYPE.EduManager_Phong &&
                    <Popover
                        content={<div className={"relative"}>
                            <Button
                                type="text"
                                icon={<CloseOutlined/>}
                                className={'absolute right-[-1.5rem] top-[-1.5rem] z-10'}
                                style={styleClose}
                                onClick={() => setIsSearchSchool(false)}
                            />
                            <Search className={"max-w-[300px]"}
                                    disabled={isRequesting}
                                    defaultValue={searchValue?.schoolName}
                                    placeholder="Nhập trường tìm kiếm"
                                    onSearch={value => onSearch(1, undefined, undefined, value)}/>
                        </div>}
                        trigger={"click"}
                        open={isSearchSchool}>
                        <SearchOutlined
                            className={"cursor-pointer text-[#0095DA]"}
                            onClick={() => {
                                setIsSearchSchool(true);
                                setIsSearchName(false);
                                setIsSearchID(false);
                            }}/>
                    </Popover>}
            </div>,
            dataIndex: "schoolName",
            key: "schoolName"
        },
        {
            title: "Điểm",
            dataIndex: "totalScore",
            key: "totalScore",
            align: "center"
        },
        {
            title: "Thời gian thi",
            dataIndex: "totalTimeDesc",
            key: "totalTimeDesc",
            align: "center"
        },
        {
            title: round1 === 0 ? "Đã tham gia" : "Vòng thi",
            render: (text: any, record) => {
                return round1 === 0 ? <div className={"flex gap-2 justify-center"}>
                    {record?.examCount}
                    <Tooltip placement="top" title={`Vòng ${record?.openRound}`}>
                        <EyeFilled className={"cursor-pointer text-[#0095DA]"}/>
                    </Tooltip>
                </div> : JSON.parse(record?.openRound || null)?.[0];
            },
            align: "center",
            key: "round"
        }
    ], [isSearchID, isSearchName, isSearchSchool, round1, searchValue, isRequesting])

    return <div className={"relative my-6 min-w-[1200px]"}>
        <TableStyle
            dataSource={selEduType === ENUM_ACCOUNT_TYPE.Student ? (rawDataTable?.userRank ? [...(rawDataTable?.DataList || []), rawDataTable?.userRank] : rawDataTable?.DataList || []) : rawDataTable?.DataList}
            columns={columns}
            bordered
            rowClassName={(selEduType === ENUM_ACCOUNT_TYPE.Student && rawDataTable?.userRank) ? "sticky-row" : ""}
            locale={{emptyText: <p className={"text-[#F24B18]"}>{rawDataTable?.message || "Không có dữ liệu"}</p>}}
            pagination={false}/>
        {isRequesting && <div className={"absolute inset-0 flex flex-col bg-black bg-opacity-20 justify-center"}>
            <Spin size={"large"}/>
        </div>}
        {(selEduType === ENUM_ACCOUNT_TYPE.EduManager_Phong && rawDataTable?.totalrow > 0) && <div className={"mt-4 flex justify-center"}>
            <Pagination current={rawDataTable.page}
                        pageSize={rawDataTable.RowPerPage}
                        showSizeChanger={false}
                        onChange={page => onSearch(page, undefined, undefined, undefined)}
                        total={rawDataTable.totalrow}/>
        </div>}
    </div>
}

const styleClose = {
    borderRadius: '50%', // Tạo button hình tròn
    backgroundColor: '#F24B18', // Màu nền
    color: 'white', // Màu chữ và icon
    padding: 0, // Loại bỏ padding mặc định
    width: 24, // Chiều rộng của button
    height: 24, // Chiều cao của button
    display: 'flex', // Đảm bảo icon nằm ở giữa
    justifyContent: 'center',
    alignItems: 'center',
}

const TableStyle = styled(Table)`
  .ant-table-thead {
    .ant-table-cell {
      font-size: 14px;
      font-weight: 700;
      color: #252525;
      background-color: rgb(227, 227, 227);
    }
  }

  .sticky-row:last-child {
    position: sticky;
    bottom: 0;
    z-index: 1; /* Đảm bảo rằng nó sẽ hiển thị ở trên các hàng khác */
    background-color: #F24B18;
    color: white;
    font-weight: bold;

    .ant-table-cell {
      background-color: #F24B18 !important;
    }
  }
`
